/**
 * Detect URLs in plain text (http/https, www., common domains).
 * Returns ordered segments: { type: 'text'|'link', value, href? }
 */

const URL_REGEX =
  /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,:;"')\]])/gi;

const GOOGLE_HOSTS = [
  "docs.google.com",
  "forms.gle",
  "drive.google.com",
  "sheets.google.com",
];

export function ensureHref(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  if (/^www\./i.test(url)) return `https://${url}`;
  return `https://${url}`;
}

export function isGoogleLink(url) {
  try {
    const href = ensureHref(url);
    const host = new URL(href).hostname.replace(/^www\./, "");
    return GOOGLE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

export function linkifyText(text) {
  if (!text || typeof text !== "string") return [];

  const segments = [];
  let lastIndex = 0;
  let match;

  const re = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: text.slice(lastIndex, match.index),
      });
    }
    const raw = match[0];
    segments.push({
      type: "link",
      value: raw,
      href: ensureHref(raw),
      isGoogle: isGoogleLink(raw),
    });
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: "text", value: text }];
}

/**
 * Classify attachment for viewer
 * @returns {'IMAGE'|'PDF'|'EXCEL'|'WORD'|'OTHER'}
 */
export function getViewerKind(file = {}, messageType) {
  if (messageType === "IMAGE" || file.fileType === "IMAGE") return "IMAGE";
  if (file.fileType === "PDF") return "PDF";
  if (file.fileType === "EXCEL") return "EXCEL";
  if (file.fileType === "WORD") return "WORD";

  const name = (file.originalName || file.url || "").toLowerCase();
  const mime = (file.mimeType || "").toLowerCase();

  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name))
    return "IMAGE";
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "PDF";
  if (
    mime.includes("spreadsheet") ||
    mime.includes("excel") ||
    /\.(xlsx|xls|csv)$/i.test(name)
  )
    return "EXCEL";
  if (
    mime.includes("word") ||
    /\.(docx|doc)$/i.test(name)
  )
    return "WORD";

  return "OTHER";
}
